import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { CheckCircle2, ArrowRight, Search, Target, TrendingUp, Bell } from 'lucide-react';
import { NetworkSnapshotForm } from '../components/NetworkSnapshotForm';
import { NEXT_STEPS } from '../constants/networkSnapshot';

const whyJoinCards = [
  { id: 'discover', title: 'Get Discovered', description: 'Be considered when relevant opportunities arise.', icon: Search },
  { id: 'relevant', title: 'Relevant Opportunities', description: 'Hear about roles aligned with your profile.', icon: Target },
  { id: 'readiness', title: 'Build Your Readiness', description: 'Understand what could strengthen your candidacy.', icon: TrendingUp },
  { id: 'connected', title: 'Stay Connected', description: 'Access relevant career updates and resources.', icon: Bell },
];

const handleReadinessClick = () => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'talent_network_to_readiness_clicked');
  }
};

const Joined = ({ member }) => {
  const next = NEXT_STEPS[member.career_challenge] || NEXT_STEPS.other;
  const firstName = member.full_name.trim().split(' ')[0];
  return (
    <div data-testid="network-complete" className="min-h-screen bg-white">
      <section className="py-16 lg:py-24">
        <div className="max-w-lg mx-auto px-4 text-center">
          <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10 text-teal-700" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Welcome to the Elevate Talent Network, {firstName}!</h1>
          <p className="text-slate-600 mb-8">Thanks for sharing your goals. We'll use them to share relevant updates and opportunities with you.</p>
          <div data-testid="network-dashboard-setup" className="bg-[#0b1f4d] text-white rounded-2xl p-6 text-left mb-4">
            <p className="text-xs font-semibold tracking-widest text-teal-300 mb-2">YOUR FREE DASHBOARD</p>
            <h2 className="text-lg font-bold mb-2">Set up your dashboard login</h2>
            <p className="text-slate-300 text-sm mb-4">We've emailed a secure link to <span className="font-semibold text-white">{member.email}</span>. Use the same email to sign in to My Career Journey and explore roles, openings and your learning path.</p>
            <Link to={`/my-journey?setup=1&email=${encodeURIComponent(member.email)}`} data-testid="network-setup-login-btn"
              className="w-full inline-flex items-center justify-center gap-2 bg-teal-400 hover:bg-teal-300 text-[#0b1f4d] py-3 rounded-full font-semibold transition-colors">
              Set up my login now <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div data-testid="network-next-step" className="bg-slate-50 border border-slate-200 rounded-2xl p-6 text-left">
            <p className="text-xs font-semibold tracking-widest text-teal-700 mb-2">YOUR NEXT STEP</p>
            <h2 className="text-lg font-bold text-slate-900 mb-2">{next.title}</h2>
            <p className="text-slate-600 text-sm mb-4">{next.text}</p>
            {next.cta && (
              <Link to={next.cta.href} onClick={handleReadinessClick} data-testid="network-next-step-cta">
                <Button className="w-full bg-teal-400 hover:bg-teal-300 text-[#0b1f4d] py-5 rounded-full font-semibold">
                  {next.cta.label} <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            )}
          </div>
          <Link to="/" className="inline-block mt-6 text-sm text-slate-500 hover:text-slate-700" data-testid="network-back-home">Back to Home</Link>
        </div>
      </section>
    </div>
  );
};

export const JoinNetwork = () => {
  const [member, setMember] = useState(null);

  if (member) return <Joined member={member} />;

  return (
    <div data-testid="network-page" className="min-h-screen bg-white">
      {/* ============================================
          HERO
          ============================================ */}
      <section className="bg-[#0b1f4d] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            {/* Left: Content */}
            <div className="text-center lg:text-left">
              <p className="text-xs font-semibold tracking-widest text-teal-300 mb-4">
                ELEVATE TALENT NETWORK · FREE TO JOIN
              </p>

              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 leading-tight">
                Be ready when the right opportunity comes along.
              </h1>

              <p className="text-lg text-slate-300 mb-6 max-w-lg mx-auto lg:mx-0">
                Create your profile and become discoverable for relevant early-career opportunities shared through the Elevate Careers network.
              </p>

              <div className="flex flex-col items-center lg:items-start">
                <a href="#join-form">
                  <Button className="bg-teal-400 text-[#0b1f4d] hover:bg-teal-300 px-8 py-6 text-lg rounded-full shadow-lg font-semibold">
                    Join the Talent Network — Free
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </a>
                <p className="text-sm text-slate-400 mt-3">
                  Takes about 2 minutes
                </p>
              </div>
            </div>

            {/* Right: Sample Profile Card */}
            <div className="hidden lg:block">
              <div className="bg-white rounded-2xl shadow-2xl overflow-hidden max-w-sm mx-auto">
                <div className="bg-slate-800 px-5 py-3">
                  <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Sample Profile</span>
                </div>
                <div className="p-5">
                  <div className="mb-4">
                    <h3 className="text-lg font-bold text-slate-900">Business Analyst</h3>
                    <p className="text-slate-500 text-sm">B.Tech · 2026</p>
                  </div>

                  <div className="space-y-3 mb-4">
                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-xs text-slate-500">Profile Strength</span>
                        <span className="text-xs font-bold text-slate-700">78%</span>
                      </div>
                      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full bg-emerald-500 rounded-full" style={{ width: '78%' }} />
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-xs text-slate-500">Interview Readiness</span>
                        <span className="text-xs font-bold text-slate-700">72%</span>
                      </div>
                      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full bg-blue-500 rounded-full" style={{ width: '72%' }} />
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <span className="text-xs bg-slate-100 text-slate-600 px-2.5 py-1 rounded-full">Excel</span>
                    <span className="text-xs bg-slate-100 text-slate-600 px-2.5 py-1 rounded-full">SQL</span>
                    <span className="text-xs bg-slate-100 text-slate-600 px-2.5 py-1 rounded-full">Communication</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================
          WHY JOIN
          ============================================ */}
      <section className="py-14 lg:py-20 bg-slate-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 text-center mb-10">
            More than another resume database.
          </h2>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {whyJoinCards.map((card) => (
              <div
                key={card.id}
                className="bg-white border border-slate-200 rounded-2xl p-5 text-center"
              >
                <div className="w-12 h-12 bg-teal-50 rounded-xl flex items-center justify-center mx-auto mb-3">
                  <card.icon className="w-6 h-6 text-teal-700" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-1">{card.title}</h3>
                <p className="text-slate-500 text-sm">{card.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="join-form" className="py-14 lg:py-20 bg-white scroll-mt-16">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Join in about 2 minutes.</h2>
            <p className="text-slate-500 text-sm">A few quick questions so we can share opportunities that actually fit you.</p>
          </div>
          <NetworkSnapshotForm onJoined={(m) => { if (m.readiness_token) sessionStorage.setItem('elevate_readiness_handoff', m.readiness_token); setMember(m); window.scrollTo({ top: 0 }); }} />
        </div>
      </section>
    </div>
  );
};

export default JoinNetwork;
